import { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight, Eye, Loader2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 20;

const STATUS_CONFIG = {
  pending:  { label: "Pending",  color: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", color: "bg-red-50 text-red-600 border-red-200" },
};

const DOC_LABELS = {
  passport: "Passport", pan_card: "PAN Card", aadhaar: "Aadhaar",
  address_proof: "Address Proof", bank_statement: "Bank Statement",
  tax_document: "Tax Document", other: "Other",
};

export default function AdminKYC() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.document_type = typeFilter;
      if (debouncedSearch) params.user_search = debouncedSearch;
      const { data } = await api.get("/kyc/admin/documents", { params });
      setItems(data.documents);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [page, statusFilter, typeFilter, debouncedSearch]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleReview = async (action) => {
    if (action === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/kyc/admin/documents/${reviewDoc.document_id}/review`, {
        action,
        rejection_reason: action === "reject" ? rejectionReason.trim() : undefined,
      });
      toast.success(`Document ${action === "approve" ? "approved" : "rejected"}`);
      setReviewDoc(null);
      setRejectionReason("");
      loadDocs();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader title="KYC Documents" subtitle={`${total} total documents`} />

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 flex flex-wrap gap-3 shadow-sm">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40" />
            <input
              type="text"
              placeholder="Search by user name or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 h-9 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50"
            />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider">
            <option value="">All Types</option>
            {Object.entries(DOC_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                  {["User", "Document Type", "File", "Status", "Uploaded", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3.5 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 animate-pulse">
                      {[...Array(6)].map((_, j) => <td key={j} className="px-4 py-4"><div className="h-4 bg-[hsl(var(--zt-beige-deep))]/50 rounded" /></td>)}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">No documents found</td></tr>
                ) : items.map(doc => {
                  const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={doc.document_id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
                      <td className="px-4 py-3.5">
                        <p className="font-semibold text-sm text-[hsl(var(--zt-teal))]">{doc.user_name || "—"}</p>
                        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{doc.user_email}</p>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/70">{DOC_LABELS[doc.document_type] || doc.document_type}</td>
                      <td className="px-4 py-3.5">
                        <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 truncate max-w-[160px]">{doc.file_name}</p>
                        <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/40">{doc.mime_type}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center text-[9px] font-heading uppercase tracking-wider px-2.5 py-1 rounded-full border font-bold ${sc.color}`}>
                          {sc.label}
                        </span>
                        {doc.rejection_reason && (
                          <p className="text-[10px] text-red-500 mt-1 max-w-[160px] truncate">{doc.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-[11px] text-[hsl(var(--zt-charcoal))]/55 whitespace-nowrap">{fmtDate(doc.created_at)}</td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => { setReviewDoc(doc); setRejectionReason(""); }}
                          className="w-7 h-7 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                          title="Review"
                        >
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--zt-beige-deep))]">
              <p className="text-xs text-[hsl(var(--zt-charcoal))]/50 font-heading">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Review Dialog */}
        <Dialog open={!!reviewDoc} onOpenChange={open => { if (!open) setReviewDoc(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-[hsl(var(--zt-teal))]">Review Document</DialogTitle>
            </DialogHeader>
            {reviewDoc && (
              <div className="space-y-4 py-2">
                <div className="bg-[hsl(var(--zt-beige))]/50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[hsl(var(--zt-charcoal))]/50 font-heading uppercase tracking-wider text-[10px]">User</span><span className="font-semibold text-[hsl(var(--zt-teal))]">{reviewDoc.user_name}</span></div>
                  <div className="flex justify-between"><span className="text-[hsl(var(--zt-charcoal))]/50 font-heading uppercase tracking-wider text-[10px]">Type</span><span>{DOC_LABELS[reviewDoc.document_type]}</span></div>
                  <div className="flex justify-between"><span className="text-[hsl(var(--zt-charcoal))]/50 font-heading uppercase tracking-wider text-[10px]">File</span><span className="truncate max-w-[200px]">{reviewDoc.file_name}</span></div>
                  <div className="flex justify-between"><span className="text-[hsl(var(--zt-charcoal))]/50 font-heading uppercase tracking-wider text-[10px]">Status</span>
                    <span className={`text-[9px] font-heading uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${STATUS_CONFIG[reviewDoc.status]?.color}`}>
                      {STATUS_CONFIG[reviewDoc.status]?.label}
                    </span>
                  </div>
                </div>

                {reviewDoc.status === "pending" ? (
                  <>
                    <div>
                      <label htmlFor="rejection-reason" className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1.5">
                        Rejection Reason <span className="text-[hsl(var(--zt-charcoal))]/35 normal-case tracking-normal">(required if rejecting)</span>
                      </label>
                      <textarea
                        id="rejection-reason"
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        rows={3}
                        placeholder="Explain why the document is being rejected…"
                        className="w-full px-3 py-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 resize-none"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={() => handleReview("approve")} disabled={submitting}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} className="mr-1.5" /> Approve</>}
                      </Button>
                      <Button onClick={() => handleReview("reject")} disabled={submitting}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <><XCircle size={14} className="mr-1.5" /> Reject</>}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 text-center py-2">
                    This document has already been {reviewDoc.status}.
                    {reviewDoc.rejection_reason && <span className="block mt-1 text-red-500">{reviewDoc.rejection_reason}</span>}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
